import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "../store";
import { fetchApplicationList } from "../slices/application";
import {
  checkTokenForCompany,
  fetchCompanyList,
  getCompany,
} from "../slices/company";
import {
  fetchAwsUser,
  companyLogin,
  clearDbUser,
  getPasskeys,
} from "../slices/auth";
import { Applications } from "../types/application";
import { setSavedRoute, updateLoadedState } from "../slices/shared";
import { getProvider } from "../slices/provider";
import { associateWebAuthnCredential } from "@aws-amplify/auth";

export const usePostLoginRouting = (authenticated: boolean) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { enabledApplications } = useSelector((state) => state.application);
  const { selectedCompany, allowedCompanies, addNewCompany } = useSelector(
    (state) => state.company
  );
  const { dbUser, awsUser, authCredentials } = useSelector(
    (state) => state.auth
  );
  const { appLoaded, savedRoute } = useSelector((state) => state.shared);
  const {
    ssoStatus,
    hasFetchedProvider,
    status: providerStatus,
  } = useSelector((state) => state.provider);

  const [postLoginNavigationComplete, setPostLoginNavigationComplete] =
    useState(false);
  const lastCompanyIdRef = useRef<number | undefined>();
  const lastFinishedSetupRef = useRef<boolean | undefined>();

  const finishedAuthLoad = useMemo(() => {
    return authenticated && !addNewCompany && appLoaded.awsUser;
  }, [addNewCompany, appLoaded.awsUser, authenticated]);

  const finishedCompanyListLoad = useMemo(() => {
    return finishedAuthLoad && awsUser && appLoaded.companyList;
  }, [appLoaded.companyList, finishedAuthLoad, awsUser]);

  const finishedSelectedCompanyLoad = useMemo(() => {
    return finishedAuthLoad && awsUser && appLoaded.dbUser;
  }, [appLoaded.dbUser, awsUser, finishedAuthLoad]);

  const finishedInitialLoad = useMemo(() => {
    return finishedCompanyListLoad || finishedSelectedCompanyLoad;
  }, [finishedCompanyListLoad, finishedSelectedCompanyLoad]);

  useEffect(() => {
    if (!authenticated) {
      setPostLoginNavigationComplete(false);
      lastCompanyIdRef.current = undefined;
      lastFinishedSetupRef.current = undefined;
    }
  }, [authenticated]);

  useEffect(() => {
    const companyId = selectedCompany?.id;

    if (!companyId) {
      lastCompanyIdRef.current = undefined;
      lastFinishedSetupRef.current = undefined;
      setPostLoginNavigationComplete(false);
      return;
    }

    if (lastCompanyIdRef.current !== companyId) {
      lastCompanyIdRef.current = companyId;
      lastFinishedSetupRef.current = selectedCompany?.finishedSetup;
      setPostLoginNavigationComplete(false);
    }
  }, [selectedCompany?.finishedSetup, selectedCompany?.id]);

  useEffect(() => {
    const finishedSetup = selectedCompany?.finishedSetup;

    if (finishedSetup === undefined) {
      lastFinishedSetupRef.current = undefined;
      return;
    }

    if (finishedSetup && lastFinishedSetupRef.current === false) {
      setPostLoginNavigationComplete(false);
    }

    lastFinishedSetupRef.current = finishedSetup;
  }, [selectedCompany?.finishedSetup]);

  useEffect(() => {
    if (savedRoute) {
      setPostLoginNavigationComplete(false);
    }
  }, [savedRoute]);

  useEffect(() => {
    if (authenticated && !finishedAuthLoad) {
      dispatch(fetchAwsUser());
    }
  }, [authenticated, dispatch, finishedAuthLoad]);

  useEffect(() => {
    if (
      finishedAuthLoad &&
      awsUser &&
      authCredentials === undefined &&
      !ssoStatus?.useSso &&
      hasFetchedProvider &&
      providerStatus !== "loading"
    ) {
      dispatch(getPasskeys());
    }
  }, [
    authCredentials,
    awsUser,
    dispatch,
    finishedAuthLoad,
    hasFetchedProvider,
    providerStatus,
    ssoStatus,
  ]);

  useEffect(() => {
    if (finishedAuthLoad && !selectedCompany && !finishedSelectedCompanyLoad) {
      dispatch(checkTokenForCompany());
    }
  }, [
    dispatch,
    finishedAuthLoad,
    selectedCompany,
    finishedSelectedCompanyLoad,
  ]);

  useEffect(() => {
    if (finishedAuthLoad && !selectedCompany && !finishedCompanyListLoad) {
      dispatch(fetchCompanyList());
    }
  }, [dispatch, finishedAuthLoad, finishedCompanyListLoad, selectedCompany]);

  useEffect(() => {
    const createPasskey = async () => {
      try {
        await associateWebAuthnCredential();
        await dispatch(getPasskeys());
      } catch {
        return;
      }
    };
    if (
      finishedInitialLoad &&
      !ssoStatus?.useSso &&
      hasFetchedProvider &&
      providerStatus !== "loading" &&
      authCredentials !== undefined &&
      authCredentials.length < 1
    ) {
      createPasskey();
    }
  }, [
    dispatch,
    finishedInitialLoad,
    authCredentials,
    ssoStatus,
    hasFetchedProvider,
    providerStatus,
  ]);

  useEffect(
    () => {
      if (finishedInitialLoad && !selectedCompany) {
        if (!allowedCompanies.length) {
          return navigate("/setup");
        }
        if (allowedCompanies.length > 1) {
          return navigate("/select", { replace: true });
        }
        if (allowedCompanies.length === 1) {
          dispatch(getCompany(allowedCompanies[0].id));
        }
      }
    },
    // disabling rule to prevent app constant re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, finishedInitialLoad, selectedCompany, allowedCompanies]
  );

  useEffect(() => {
    if (!finishedInitialLoad || !selectedCompany?.id || !!dbUser) return;

    const loadCompany = async () => {
      await dispatch(companyLogin(selectedCompany.id));
      await dispatch(getCompany(selectedCompany.id));
      await dispatch(getProvider());
    };

    loadCompany();
  }, [dbUser, dispatch, finishedInitialLoad, selectedCompany?.id]);

  useEffect(() => {
    if (
      finishedInitialLoad &&
      !!selectedCompany?.finishedSetup &&
      !!dbUser &&
      enabledApplications?.length < 1 &&
      !appLoaded.appList
    ) {
      dispatch(fetchApplicationList());
    }
  }, [
    appLoaded.appList,
    dbUser,
    dispatch,
    enabledApplications?.length,
    finishedInitialLoad,
    selectedCompany?.finishedSetup,
  ]);

  useEffect(
    () => {
      if (!selectedCompany?.id && dbUser) {
        dispatch(updateLoadedState({ ...appLoaded, dbUser: false }));
        dispatch(clearDbUser());
      }
    },
    // disabling rule to prevent app constant re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompany?.id, dbUser, dispatch]
  );

  useEffect(
    () => {
      if (
        finishedInitialLoad &&
        selectedCompany &&
        dbUser &&
        appLoaded.appList
      ) {
        if (postLoginNavigationComplete) {
          return;
        }

        if (!selectedCompany?.finishedSetup || !dbUser) {
          setPostLoginNavigationComplete(true);
          return navigate("/setup");
        } else if (!!savedRoute && enabledApplications.length > 0) {
          const route = savedRoute;
          dispatch(setSavedRoute(undefined));
          setPostLoginNavigationComplete(true);
          switch (route) {
            case "detector":
              const detector = enabledApplications.find(
                (x) => x.id === Applications.Detector
              );
              window.location.href = detector?.url ?? "";
              break;
            case "gmd":
              const gmd = enabledApplications.find(
                (x) => x.id === Applications.Gmd
              );
              window.location.href = gmd?.url ?? "";
              break;
            case "responder":
              const responder = enabledApplications.find(
                (x) => x.id === Applications.Responder
              );
              window.location.href = responder?.url ?? "";
              break;
            case "investigator":
              const investigator = enabledApplications.find(
                (x) => x.id === Applications.Investigator
              );
              window.location.href = investigator?.url ?? "";
              break;
            default:
              return navigate(`/${route}`);
          }
        } else if (enabledApplications.length <= 1 && appLoaded.appList) {
          setPostLoginNavigationComplete(true);
          return navigate("/account");
        } else {
          setPostLoginNavigationComplete(true);
          return navigate("/apps");
        }
      }

      return;
    },
    // disabling rule to prevent app constant re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appLoaded.appList,
      dbUser,
      dispatch,
      enabledApplications,
      finishedInitialLoad,
      postLoginNavigationComplete,
      selectedCompany,
      savedRoute,
    ]
  );
};
